/**
 * What the pointer does on the graph canvas: pan, zoom (wheel and pinch), move a node, select
 * a node or a link, draw a new link from a node's handle, drag an end of the selected link to
 * reconnect it, Alt+click a link to delete it. A group is grabbed by its rim or its name: a
 * click selects it, a drag moves all its nodes (clicking inside the circle still pans). A
 * click on a bundle of links between groups selects the bundle.
 *
 * In the 3D view, dragging the background turns the camera around the graph (Ctrl+drag pans),
 * and a dragged node or group keeps its depth: it moves on the plane facing the viewer.
 *
 * The middle mouse button moves the view from anywhere, over a node, a link or a group too
 * (in 3D as well: it pans, never turns), and a middle click selects nothing.
 *
 * The controller only keeps short-lived gesture state and reports results through callbacks;
 * it never sends anything to the core itself (see `edits.ts`) and draws nothing (see
 * `picture.ts`). It works on screen coordinates relative to the canvas.
 */
import type { Link } from "@protocol/Link";
import { orbit, type Vec3 } from "../../state/camera";
import type { Focus, Scene, SceneBundle, SceneGroup, SimLink, SimNode } from "../../state/scene";
import { handlePosition, type Linking, type Reconnecting } from "./picture";

/** A screen point relative to the canvas. */
export interface Point {
  x: number;
  y: number;
}

/** A pointer drag that may become a node move, a pan, or stay a click. */
interface Drag {
  node: SimNode | null;
  link: SimLink | null;
  group: SceneGroup | null;
  bundle: SceneBundle | null;
  /** The last pointer position in world coordinates (to move a group by the difference). */
  world: Vec3;
  alt: boolean;
  /** In 3D, the background drag pans instead of turning (Ctrl was held, or the middle button). */
  pan: boolean;
  /** Pressed with the middle button: only moves the view, and a click does nothing. */
  middle: boolean;
  /** The previous pointer position (to turn the 3D camera by the difference). */
  lastX: number;
  lastY: number;
  startX: number;
  startY: number;
  viewX: number;
  viewY: number;
  moved: boolean;
}

/** What the controller needs from outside, and what it reports. */
export interface PointerDeps {
  scene: Scene;
  /** The selected node and link, as scene objects. */
  selectedNode(): SimNode | null;
  selectedLink(): SimLink | null;
  /** What the user is looking at (decides which bundles are open, so which links can be clicked). */
  focus(): Focus;
  /** `true` when the link tool is on (a drag on a node draws a link). */
  linkTool(): boolean;
  /** The pointer's look (`grab`, `pointer`, `crosshair`). */
  setCursor(cursor: string): void;
  /** Something visible changed: repaint. */
  invalidate(): void;
  /** A click selected a node (`null`: the background was clicked). */
  selectNode(id: string | null): void;
  /** A click selected a link. */
  selectLink(link: Link): void;
  /** A click on a group's rim or name selected it. */
  selectGroup(id: string): void;
  /** A click on a bundle of links selected it. */
  selectBundle(key: string): void;
  /** Alt+click on a link. */
  removeLink(link: Link): void;
  /** A link was drawn from one node to another, released at `at`. */
  linked(from: SimNode, to: SimNode, at: Point): void;
  /** A link was drawn from a node and released on empty space at `at` (a node to add there). */
  linkedToNothing(from: SimNode, at: Point): void;
  /** An end of a link was dropped on another node. */
  reconnected(gesture: Reconnecting): void;
  /** A node, or a group's nodes, are being dragged (called on every move). */
  dragged(nodes: readonly SimNode[]): void;
  /** A node, or a group's nodes, were dragged to a new place. */
  nodeMoved(): void;
  /** A press started (closes the link-kind menu). */
  pressed(): void;
}

/** What is under a point of the canvas, for the right-click menu. */
export type Target =
  | { type: "node"; node: SimNode }
  | { type: "link"; link: SimLink }
  | { type: "group"; group: SceneGroup }
  | { type: "bundle"; bundle: SceneBundle }
  | { type: "background" };

/** The pointer state and handlers to attach to the canvas. */
export interface PointerController {
  /** What a click at `p` would pick (same priority as a left click). */
  targetAt(p: Point): Target;
  /** The node under a mouse pointer (drawn highlighted). */
  hover(): SimNode | null;
  /** The link under a mouse pointer. */
  hoverLink(): SimLink | null;
  /** The group whose rim or name is under a mouse pointer. */
  hoverGroup(): SceneGroup | null;
  /** The bundle under a mouse pointer. */
  hoverBundle(): SceneBundle | null;
  /** A link being drawn, if any. */
  linking(): Linking | null;
  /** A link end being dragged, if any. */
  reconnecting(): Reconnecting | null;
  /** `true` while a node or a group is being dragged (handles are hidden). */
  draggingNode(): boolean;
  /** Stops drawing or reconnecting a link. Returns `true` if there was one. */
  cancel(): boolean;
  /** Forgets the hover (the pointer left the canvas). */
  leave(): void;
  down(e: PointerEvent, p: Point): void;
  move(e: PointerEvent, p: Point): void;
  /** Pointer released or cancelled (`e.type === "pointercancel"` aborts the gesture). */
  up(e: PointerEvent, p: Point): void;
  wheel(e: WheelEvent, p: Point): void;
}

/** `PointerEvent.button` of the middle mouse button (the wheel pressed): moves the view from anywhere. */
export const MIDDLE = 1;

/** How close (in pixels) the pointer must be to a handle or a link end to grab it. */
const GRAB_RADIUS = 9;

/** How far (in pixels) a press must move before it counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

/** How much the 3D camera turns per pixel of drag, in radians. */
const TURN_PER_PIXEL = 0.008;

/** Creates the controller. */
export function createPointer(deps: PointerDeps): PointerController {
  const { scene } = deps;
  let hover: SimNode | null = null;
  let hoverLink: SimLink | null = null;
  let hoverGroup: SceneGroup | null = null;
  let hoverBundle: SceneBundle | null = null;
  let linking: Linking | null = null;
  /** Where the link being drawn started (a release close to it is not a drop on empty space). */
  let linkStart: Point | null = null;
  let reconnecting: Reconnecting | null = null;
  let drag: Drag | null = null;
  let pinch: { distance: number; k: number } | null = null;
  const pointers = new Map<number, Point>();

  /** The resting cursor. */
  const idleCursor = () => (deps.linkTool() ? "crosshair" : "grab");

  /** The two first active pointers (for a pinch). */
  const pair = () => [...pointers.values()] as [Point, Point];

  /** Which end of the selected link is under the pointer. */
  function endAt(p: Point): "a" | "b" | null {
    const l = deps.selectedLink();
    if (!l?.visible) return null;
    const s = scene.segment(l);
    if (!s) return null;
    if (Math.hypot(p.x - s.x1, p.y - s.y1) <= GRAB_RADIUS) return "a";
    if (Math.hypot(p.x - s.x2, p.y - s.y2) <= GRAB_RADIUS) return "b";
    return null;
  }

  /** The node whose link handle is under the pointer. */
  function handleAt(p: Point): SimNode | null {
    for (const n of [hover, deps.selectedNode()]) {
      if (!n?.visible) continue;
      const h = handlePosition(scene, n);
      if (Math.hypot(h.x - p.x, h.y - p.y) <= GRAB_RADIUS) return n;
    }
    return null;
  }

  /** Starts a gesture: pinch, reconnect, link, or a drag that may stay a click. */
  function down(e: PointerEvent, p: Point): void {
    if (e.pointerType === "mouse" && e.button === MIDDLE) {
      panFromAnywhere(e, p);
      return;
    }
    // Only the main mouse button drags and selects; the right one opens the menu.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pointers.set(e.pointerId, p);
    if (pointers.size === 2) {
      const [a, b] = pair();
      pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y) || 1, k: scene.view.k };
      if (drag?.node) drag.node.fixed = false;
      drag = null;
      return;
    }
    deps.pressed();
    const selected = deps.selectedLink();
    const end = endAt(p);
    if (end && selected) {
      reconnecting = { link: selected, end, x: p.x, y: p.y, to: null };
      hover = null;
      deps.setCursor("crosshair");
      deps.invalidate();
      return;
    }
    const fromHandle = handleAt(p);
    const labelled = fromHandle ? null : scene.hitGroupLabel(p.x, p.y);
    const counted = fromHandle || labelled ? null : scene.hitBundleCount(p.x, p.y, deps.focus());
    const node = labelled || counted ? null : scene.hitNode(p.x, p.y);
    if (fromHandle || (node && (deps.linkTool() || e.shiftKey))) {
      linking = { from: (fromHandle ?? node)!, x: p.x, y: p.y, to: null };
      linkStart = p;
      hover = null;
      deps.setCursor("crosshair");
      deps.invalidate();
      return;
    }
    const link = node || labelled || counted ? null : scene.hitLink(p.x, p.y, deps.focus());
    const bundle = counted ?? (node || labelled || link ? null : scene.hitBundle(p.x, p.y, deps.focus()));
    const group = labelled ?? (node || link || bundle ? null : scene.hitGroup(p.x, p.y));
    const world = group ? scene.planePoint(p.x, p.y, group) : scene.toWorld(p.x, p.y);
    drag = { node, link, group, bundle, world, alt: e.altKey, pan: e.ctrlKey || e.metaKey, middle: false, lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, viewX: scene.view.x, viewY: scene.view.y, moved: false };
    if (node) node.fixed = true;
    for (const n of group?.all ?? []) n.fixed = true;
  }

  /**
   * Starts moving the view with the middle button, whatever is under the pointer (nothing is
   * grabbed, selected or linked). Ignored while another gesture runs.
   */
  function panFromAnywhere(e: PointerEvent, p: Point): void {
    // Not the browser's own use of the button (autoscroll, pasting on Linux).
    e.preventDefault();
    if (drag || linking || reconnecting || pinch) return;
    pointers.set(e.pointerId, p);
    deps.pressed();
    hover = null;
    drag = { node: null, link: null, group: null, bundle: null, world: scene.toWorld(p.x, p.y), alt: false, pan: true, middle: true, lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, viewX: scene.view.x, viewY: scene.view.y, moved: false };
    deps.setCursor("grabbing");
    deps.invalidate();
  }

  /** Follows the gesture in progress, or updates the hover. */
  function move(e: PointerEvent, p: Point): void {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, p);
    if (pinch && pointers.size >= 2) {
      const [a, b] = pair();
      const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      scene.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, (pinch.k * distance) / pinch.distance / scene.view.k);
      deps.invalidate();
      return;
    }
    if (reconnecting) {
      reconnecting = { ...reconnecting, x: p.x, y: p.y, to: scene.hitNode(p.x, p.y) };
      deps.invalidate();
      return;
    }
    if (linking) {
      const target = scene.hitNode(p.x, p.y);
      linking = { ...linking, x: p.x, y: p.y, to: target && target !== linking.from ? target : null };
      deps.invalidate();
      return;
    }
    if (drag) {
      moveDrag(drag, p);
      return;
    }
    if (e.pointerType === "mouse") updateHover(p);
  }

  /**
   * Moves the dragged node or group, or moves the view when the background is dragged: pans
   * it in 2D (and with Ctrl in 3D), turns the camera in 3D.
   */
  function moveDrag(d: Drag, p: Point): void {
    const dx = p.x - d.startX;
    const dy = p.y - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) d.moved = true;
    if (!d.moved) return;
    if (d.node) {
      scene.dragTo(d.node, p.x, p.y);
      scene.reheat(0.3);
      deps.dragged([d.node]);
    } else if (d.group) {
      const w = scene.planePoint(p.x, p.y, d.group);
      scene.moveGroup(d.group, w.x - d.world.x, w.y - d.world.y, w.z - d.world.z);
      d.world = w;
      scene.reheat(0.3);
      deps.dragged(d.group.all);
    } else if (scene.dimension === "3d" && !d.pan) {
      orbit(scene.camera, (p.x - d.lastX) * TURN_PER_PIXEL, (p.y - d.lastY) * TURN_PER_PIXEL);
    } else {
      scene.view.x = d.viewX + dx;
      scene.view.y = d.viewY + dy;
    }
    d.lastX = p.x;
    d.lastY = p.y;
    deps.invalidate();
  }

  /** Highlights what is under a mouse pointer and picks the cursor. */
  function updateHover(p: Point): void {
    const labelled = scene.hitGroupLabel(p.x, p.y);
    const counted = labelled ? null : scene.hitBundleCount(p.x, p.y, deps.focus());
    let n = labelled || counted ? null : scene.hitNode(p.x, p.y);
    if (!n && hover && handleAt(p) === hover) n = hover;
    if (n !== hover) {
      hover = n;
      deps.invalidate();
    }
    const end = endAt(p);
    const l = !n && !end && !labelled && !counted ? scene.hitLink(p.x, p.y, deps.focus()) : null;
    if (l !== hoverLink) {
      hoverLink = l;
      deps.invalidate();
    }
    const b = counted ?? (!n && !end && !labelled && !l ? scene.hitBundle(p.x, p.y, deps.focus()) : null);
    if (b !== hoverBundle) {
      hoverBundle = b;
      deps.invalidate();
    }
    const g = labelled ?? (!n && !end && !l && !b ? scene.hitGroup(p.x, p.y) : null);
    if (g !== hoverGroup) {
      hoverGroup = g;
      deps.invalidate();
    }
    deps.setCursor(end || handleAt(p) || deps.linkTool() ? "crosshair" : n || l || b ? "pointer" : g ? "move" : "grab");
  }

  /** Ends the gesture and reports what it did. */
  function up(e: PointerEvent, p: Point): void {
    if (!pointers.delete(e.pointerId)) return; // a press this controller ignored
    const cancelled = e.type === "pointercancel";
    if (reconnecting) {
      const r = reconnecting;
      reconnecting = null;
      deps.invalidate();
      deps.setCursor(idleCursor());
      if (r.to && !cancelled) deps.reconnected(r);
      return;
    }
    if (linking) {
      const l = linking;
      linking = null;
      deps.invalidate();
      deps.setCursor(idleCursor());
      const far = linkStart ? Math.hypot(p.x - linkStart.x, p.y - linkStart.y) > DRAG_THRESHOLD * 4 : false;
      linkStart = null;
      if (cancelled) return;
      if (l.to) deps.linked(l.from, l.to, p);
      else if (far && !scene.hitNode(p.x, p.y)) deps.linkedToNothing(l.from, p);
      return;
    }
    if (pinch) {
      if (pointers.size < 2) pinch = null;
      drag = null;
      return;
    }
    const d = drag;
    drag = null;
    if (!d) return;
    if (d.middle) {
      deps.setCursor(idleCursor());
      return;
    }
    if (d.node) d.node.fixed = false;
    for (const n of d.group?.all ?? []) n.fixed = false;
    if (d.moved) {
      if (d.node || d.group) deps.nodeMoved();
    } else if (d.alt && d.link) {
      deps.removeLink(d.link.link);
    } else if (d.node) {
      deps.selectNode(d.node.id);
    } else if (d.link) {
      deps.selectLink(d.link.link);
    } else if (d.bundle) {
      deps.selectBundle(d.bundle.key);
    } else if (d.group) {
      deps.selectGroup(d.group.id);
    } else {
      deps.selectNode(null);
    }
  }

  return {
    targetAt(p) {
      const focus = deps.focus();
      const labelled = scene.hitGroupLabel(p.x, p.y);
      if (labelled) return { type: "group", group: labelled };
      const counted = scene.hitBundleCount(p.x, p.y, focus);
      if (counted) return { type: "bundle", bundle: counted };
      const node = scene.hitNode(p.x, p.y);
      if (node) return { type: "node", node };
      const link = scene.hitLink(p.x, p.y, focus);
      if (link) return { type: "link", link };
      const bundle = scene.hitBundle(p.x, p.y, focus);
      if (bundle) return { type: "bundle", bundle };
      const group = scene.hitGroup(p.x, p.y);
      return group ? { type: "group", group } : { type: "background" };
    },
    hover: () => hover,
    hoverLink: () => hoverLink,
    hoverGroup: () => hoverGroup,
    hoverBundle: () => hoverBundle,
    linking: () => linking,
    reconnecting: () => reconnecting,
    draggingNode: () => !!drag?.moved && (!!drag.node || !!drag.group),
    cancel() {
      if (!linking && !reconnecting) return false;
      linking = null;
      reconnecting = null;
      deps.invalidate();
      return true;
    },
    leave() {
      if (!hover && !hoverLink && !hoverGroup && !hoverBundle) return;
      hover = null;
      hoverLink = null;
      hoverGroup = null;
      hoverBundle = null;
      deps.invalidate();
    },
    down,
    move,
    up,
    wheel(e, p) {
      e.preventDefault();
      scene.zoomAt(p.x, p.y, Math.exp(-e.deltaY * 0.0016));
      deps.invalidate();
    },
  };
}
